import {DateTime} from 'luxon';
import {compose} from 'redux';

import {addQueryToPath} from '../../../../libs/shared/helpers/src/browser/urlHelpers';
import {ExtendedError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {
  isUnsavedDraftCalendarEventVersion,
  makeStartEndDateTimeForCalendarEventVersion,
  trackSaveInterfaceEvent,
} from '../../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
  createUpdater,
} from '../../../../srcCommon/helpers/updateHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {buildCalendarEventVersionStartEnd, StartEndData} from '../../../helpers/calendarHelpers';
import {findTimeZone} from '../../../helpers/dateTimeHelpers';
import {buildParticipantFromVisualParticipant} from '../../../helpers/models/participantHelpers';
import {
  applyInstanceChangesToMaster,
  buildExceptionEventFromMaster,
  isRecurringExceptionEvent,
  isRecurringMasterEvent,
  isRecurringSaveTypeRequired,
  retrieveMasterEventForId,
} from '../../../helpers/models/recurringEventHelpers';
import {createUpdatableResourceUpdater} from '../../../helpers/models/resourceHelpers';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {CalendarEventModel, CalendarEventVisualStatusesEnum} from '../../../models/api/calendarEventModel';
import {
  CalendarEventVersionModel,
  CalendarEventVersionResponse,
  serializeCalendarEventVersion,
} from '../../../models/api/calendarEventVersionModel';
import {CalendarVersionModel} from '../../../models/api/calendarVersionModel';
import {CompanyModel} from '../../../models/api/companyModel';
import {ContactModel} from '../../../models/api/contactModel';
import {ApiCalendarEventVersionDataGroup} from '../../../models/api/dataGroupModel';
import {ParticipantResponseStatusesEnum} from '../../../models/api/participantModel';
import {VisualParticipantModel} from '../../../models/api/visualParticipantModel';
import {deleteApiAsync, postApiAsync, putApiAsync} from '../../../services/http/http';
import {HttpError} from '../../../services/http/httpError';
import {lockAllAsync} from '../../../services/locks/lock';
import {findCalendarEventLock, findCalendarEventVersionLock} from '../../../services/locks/resourceLocks';
import {AppState} from '../../appState';
import {Dispatch} from '../../appStore';
import {ThunkAction} from '../../appThunk';
import {ResourceMap} from '../../appTypes';
import {optimisticUpdateAsyncAction} from '../../optimisticUpdates/optimisticUpdatesActions';
import {CalendarEventData, makeGetCalendarEventData} from '../../panels/calendars/calendarsPanelSelectors';
import {CalendarEventGridParameters} from '../../panels/calendars/calendarsPanelState';
import {getCompany} from '../company/companySelectors';
import {makeGetVisualParticipants} from '../contacts/participantsSelectors';
import {updateDataAction} from '../dataGroupsActions';
import {
  getCalendarEvents,
  getCalendarEventVersions,
  getCalendars,
  getCalendarVersions,
  getContacts,
  getMasterCalendarEventIdToUidMap,
  getOriginalData,
} from '../dataSelectors';
import {DataState} from '../dataState';
import {getUserNamespace} from '../teammates/teammatesSelectors';

/*
 * Action types.
 */

export enum CalendarEventVersionActionsEnum {
  CALENDAR_EVENT_VERSION_DELETED = '@@data/calendarEventVersions/CALENDAR_EVENT_VERSION_DELETED',
}

/*
 * Types.
 */

export enum RecurringEventSaveTypesEnum {
  ENTIRE_SERIES = 'entire_series',
  INSTANCE = 'instance',
}

export interface CalendarEventSaveData {
  newCalendarEventVersion: CalendarEventVersionModel;
  newCalendarEvent: CalendarEventModel;
  calendarVersion: CalendarVersionModel;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
  instanceStartDate?: number;
  recurringSaveType?: RecurringEventSaveTypesEnum;
  isRsvp?: boolean;
  colorIsNop?: boolean;
  shouldNotifyGuests?: boolean;
}

export interface CalendarEventDeleteData {
  calendarEventData: CalendarEventData;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
  recurringSaveType: RecurringEventSaveTypesEnum | undefined;
  shouldNotifyGuests?: boolean;
}

export interface CalendarEventVersionDeletedAction extends ActionWithPayload<string> {
  type: CalendarEventVersionActionsEnum.CALENDAR_EVENT_VERSION_DELETED;
}

/*
 * Action creators.
 */

interface ReplyToCalendarEventOptions {
  calendarEventVersionUid: string;
  response: CalendarEventVersionResponse;
  channelId?: number;
  calendarVersionId?: number;
  recurringSaveType?: RecurringEventSaveTypesEnum;
  instanceStartDate?: number;
  shouldSkipNotifications?: boolean;
}

interface CalendarEventResponseData {
  calendarEventVersion: CalendarEventVersionModel;
  calendarEvent: CalendarEventModel;
  response: CalendarEventVersionResponse;
  recurringSaveType?: RecurringEventSaveTypesEnum;
  calendarVersion?: CalendarVersionModel;
  instanceStartDate?: number;
  needToCreateRecurringInstance?: boolean;
  masterCalendarEvent?: CalendarEventModel;
  masterCalendarEventVersion?: CalendarEventVersionModel;
  needsFollowUpReplyToRecurringInstance?: boolean;
}

// TODO: Patch the resource dynamically (such as by calling `buildCalendarEventVersionUpdates`) inside the optimistic updater.
function createOptimisiticUpdaterForReply(
  newCalendarEvent: CalendarEventModel,
  newCalendarEventVersion: CalendarEventVersionModel,
) {
  const optimisticUpdaterForCalendarEvent = createObjectPropertyUpdater<DataState, 'calendarEvents'>(
    'calendarEvents',
    createResourceMapValueUpdater(newCalendarEvent.uid, createUpdatableResourceUpdater(newCalendarEvent)),
  );

  const optimisticUpdaterForCalendarEventVersions = createObjectPropertyUpdater<
    DataState,
    'calendarEventVersions'
  >(
    'calendarEventVersions',
    createResourceMapValueUpdater(
      newCalendarEventVersion.uid,
      createUpdatableResourceUpdater(newCalendarEventVersion),
    ),
  );

  return compose(optimisticUpdaterForCalendarEvent, optimisticUpdaterForCalendarEventVersions);
}

/** Update response status for the calendar event of a message. */
export function replyToCalendarEventVersionAction(options: ReplyToCalendarEventOptions): ThunkAction {
  return async (dispatch, getState) => {
    const state = getState();
    const {
      calendarEventVersionUid,
      calendarVersionId,
      channelId,
      instanceStartDate,
      response,
      recurringSaveType,
      shouldSkipNotifications = false,
    } = options;

    if (!channelId && !calendarVersionId) {
      throw new Error('A channel or calendarVersion is required to reply to an event.');
    }

    const calendarEventVersion = calendarEventVersionUid
      ? getCalendarEventVersions(state)[calendarEventVersionUid]
      : undefined;
    if (!calendarEventVersion) {
      throw new Error('A calendar event version with this UID could not be found.');
    }

    const calendarEvent = getCalendarEvents(state)[calendarEventVersion.calendarEventUid];
    if (!calendarEvent) {
      throw new Error('A calendar event with this UID could not be found.');
    }

    const contacts = getContacts(state);

    const [masterCalendarEvent, masterCalendarEventVersion] =
      // Only fetch master calendar event when recurringSaveType is defined and in the ENTIRE series
      // case (which is the only case we need it at this time).
      recurringSaveType === RecurringEventSaveTypesEnum.ENTIRE_SERIES &&
      calendarVersionId &&
      calendarEvent.recurringCalendarEventId
        ? retrieveMasterEventForId(
            getMasterCalendarEventIdToUidMap(state),
            getCalendarEvents(state),
            getCalendarEventVersions(state),
            calendarEvent.recurringCalendarEventId,
            calendarVersionId,
          )
        : [];

    const calendarVersion = calendarVersionId ? getCalendarVersions(state)[calendarVersionId] : undefined;

    const {
      calendarEvent: newCalendarEvent,
      calendarEventVersion: newCalendarEventVersion,
      instanceStartDate: validatedInstanceStartDate,
      needToCreateRecurringInstance,
      needsFollowUpReplyToRecurringInstance,
    } = prepareResponseData(contacts, {
      calendarEvent,
      calendarEventVersion,
      recurringSaveType,
      calendarVersion,
      response,
      instanceStartDate,
      masterCalendarEvent,
      masterCalendarEventVersion,
    });

    if (needToCreateRecurringInstance && calendarVersion) {
      const getVisualParticipants = makeGetVisualParticipants(getUserNamespace, () => newCalendarEvent);
      const visualParticipants = getVisualParticipants(state, {});

      return dispatch(
        saveCalendarEventVersionAction({
          calendarVersion,
          newCalendarEventVersion,
          newCalendarEvent,
          visualParticipants,
          instanceStartDate: validatedInstanceStartDate,
          recurringSaveType: RecurringEventSaveTypesEnum.INSTANCE,
          isRsvp: true,
        }),
      );
    }

    const optimisticUpdater = createOptimisiticUpdaterForReply(newCalendarEvent, newCalendarEventVersion);
    const calendarLocks = [
      findCalendarEventVersionLock(newCalendarEventVersion.uid),
      findCalendarEventLock(newCalendarEvent.uid),
    ];

    return dispatch(
      optimisticUpdateAsyncAction(
        optimisticUpdater,
        async () =>
          await lockAllAsync(calendarLocks, async () => {
            try {
              const updateResponseStatusUrl = `${newCalendarEventVersion.calendarEventVersionUrl}/reply`;
              const serverDataGroup = await postApiAsync(
                ApiCalendarEventVersionDataGroup,
                updateResponseStatusUrl,
                {
                  channel_id: channelId,
                  calendar_version_id: calendarVersionId,
                  response_status: response.status,
                  comment: response.comment,
                  timezone: findTimeZone() || DateTime.local().zoneName,
                  send_notifications: !shouldSkipNotifications,
                },
              );

              if (needsFollowUpReplyToRecurringInstance) {
                if (!isRecurringExceptionEvent(calendarEvent)) {
                  throw Error(
                    'Follow up reply to recurring instance only expected for instance calendar events',
                  );
                }

                dispatch(
                  replyToCalendarEventVersionAction({
                    ...options,
                    recurringSaveType: RecurringEventSaveTypesEnum.INSTANCE,
                    shouldSkipNotifications: true,
                  }),
                );
              }

              return updateDataAction(serverDataGroup);
            } catch (error) {
              if (!(error instanceof HttpError)) {
                reportToBugsnag(error);
              }
              throw error;
            }
          }),
      ),
    );
  };
}

function applyResponse(
  contacts: ResourceMap<ContactModel>,
  data: CalendarEventResponseData,
): CalendarEventResponseData {
  const {calendarEvent, calendarEventVersion, response} = data;

  const newCalendarEventVersion = {
    ...calendarEventVersion,
    ...buildCalendarEventVersionUpdates(calendarEventVersion.response, response.status, response.comment),
  };

  const newCalendarEvent = {
    ...calendarEvent,
    participants: calendarEvent.participants.map((p) => {
      const contact = contacts[p.contactUid];
      if (contact && contact.handle === response.address) {
        return {
          ...p,
          responseStatus: response.status,
        };
      }

      return p;
    }),
  };

  return {
    ...data,
    calendarEvent: newCalendarEvent,
    calendarEventVersion: newCalendarEventVersion,
  };
}

function prepareResponseData(
  contacts: ResourceMap<ContactModel>,
  data: CalendarEventResponseData,
): CalendarEventResponseData {
  const {
    calendarEvent,
    calendarEventVersion,
    calendarVersion,
    instanceStartDate,
    recurringSaveType,
    masterCalendarEvent,
    masterCalendarEventVersion,
  } = data;

  // If we don't have calendarVersion, we don't support recurring response data and fall back on
  // original response data.
  if (!calendarVersion) {
    return applyResponse(contacts, data);
  }

  switch (recurringSaveType) {
    case RecurringEventSaveTypesEnum.INSTANCE: {
      // If we're updating the instance, we have to create a placeholder event to propagate.
      if (isRecurringMasterEvent(calendarEvent)) {
        if (!instanceStartDate) {
          throw new Error('An instance start date is required to RSVP to an exception event.');
        }

        const newCalendarEventVersion = {
          ...calendarEventVersion,
          ...buildCalendarEventVersionStartEndFromInstanceStart(calendarEventVersion, instanceStartDate),
          isSaving: true,
        };

        // If we have a master and are responding to an instance, we need to create a new recurring
        // instance. Update response data accordingly.
        return applyResponse(contacts, {
          ...data,
          calendarEventVersion: newCalendarEventVersion,
          needToCreateRecurringInstance: true,
        });
      }

      if (isRecurringExceptionEvent(calendarEvent)) {
        return applyResponse(contacts, data);
      }

      throw new Error('Cannot RSVP to recurring instance event without master or exception.');
    }
    case RecurringEventSaveTypesEnum.ENTIRE_SERIES: {
      // If we're updating the entire series, we have to perform the update on the master event.
      if (isRecurringMasterEvent(calendarEvent)) {
        return applyResponse(contacts, data);
      }

      if (isRecurringExceptionEvent(calendarEvent)) {
        if (!masterCalendarEvent || !masterCalendarEventVersion) {
          throw new Error('Instance event with master required to prepare master event response');
        }

        const newData = {
          ...data,
          calendarEvent: masterCalendarEvent,
          calendarEventVersion: masterCalendarEventVersion,
          // Asynchronously apply the response on the *selected* calendar event as well (so there
          // isn't a delay before the change is reflected [as we wait for the provider]). Note: We
          // currently don't support this in the editing of recurring event series, but _do_ support
          // it here.
          needsFollowUpReplyToRecurringInstance: true,
        };
        return applyResponse(contacts, newData);
      }

      throw new Error('Cannot RSVP to an entire series without a master or exception event.');
    }
    default:
      // If there are no special recurring cases to be handled, just return the original RSVP data.
      return applyResponse(contacts, data);
  }
}

function buildCalendarEventVersionStartEndFromInstanceStart(
  calendarEventVersion: CalendarEventVersionModel,
  instanceStartDate: number,
) {
  const {startDateTime, endDateTime} = makeStartEndDateTimeForCalendarEventVersion(
    calendarEventVersion,
    DateTime.fromMillis(instanceStartDate),
  );

  return buildCalendarEventVersionStartEnd({
    startDateTime,
    endDateTime,
    isAllDay: calendarEventVersion.isAllDay,
  });
}

function buildCalendarEventVersionUpdates(
  existingResponse: CalendarEventVersionResponse | undefined,
  newResponseStatus: ParticipantResponseStatusesEnum,
  newComment: string | undefined,
) {
  if (!existingResponse) {
    return {};
  }

  return {
    response: {
      ...existingResponse,
      status: newResponseStatus,
      comment: newComment,
    },
  };
}

function createOptimisticUpdaterForSave(saveData: CalendarEventSaveData) {
  const {newCalendarEvent, newCalendarEventVersion, visualParticipants} = saveData;

  const optimisticUpdaterForCalendarEvent = createObjectPropertyUpdater<DataState, 'calendarEvents'>(
    'calendarEvents',
    createResourceMapValueUpdater(newCalendarEvent.uid, createUpdatableResourceUpdater(newCalendarEvent)),
  );

  const optimisticUpdaterForCalendarEventVersions = createObjectPropertyUpdater<
    DataState,
    'calendarEventVersions'
  >(
    'calendarEventVersions',
    createResourceMapValueUpdater(
      newCalendarEventVersion.uid,
      createUpdatableResourceUpdater(newCalendarEventVersion),
    ),
  );

  const optimisticUpdaterForContacts = createObjectPropertyUpdater<DataState, 'contacts'>(
    'contacts',
    createUpdater<ResourceMap<ContactModel>>((contacts) =>
      visualParticipants.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.contact.uid]: curr.contact,
        }),
        contacts,
      ),
    ),
  );

  return compose(
    optimisticUpdaterForCalendarEvent,
    optimisticUpdaterForCalendarEventVersions,
    optimisticUpdaterForContacts,
  );
}

export function saveCalendarEventVersionAction(data: CalendarEventSaveData): ThunkAction {
  return async (dispatch, getState) => {
    const state = getState();
    const saveData = prepareSaveData(data, state);
    const {newCalendarEvent, newCalendarEventVersion, visualParticipants, calendarVersion} = saveData;
    const optimisticUpdater = createOptimisticUpdaterForSave(saveData);
    const calendarLocks = [
      findCalendarEventVersionLock(newCalendarEventVersion.uid),
      findCalendarEventLock(newCalendarEvent.uid),
    ];
    await dispatch(
      optimisticUpdateAsyncAction(
        optimisticUpdater,
        async () =>
          await lockAllAsync(calendarLocks, async () => {
            const {calendarEventVersions, calendarEvents} = getOriginalData(getState());

            const currentCalendarEventVersion =
              calendarEventVersions[newCalendarEventVersion.uid] || newCalendarEventVersion;
            if (!currentCalendarEventVersion) {
              throw new Error('A calendar event version with this UID could not be found.');
            }

            const currentCalendarEvent = calendarEvents[newCalendarEvent.uid] || newCalendarEvent;
            if (!currentCalendarEvent) {
              throw new Error('A calendar event with this UID could not be found.');
            }

            const body = serializeCalendarEventVersion(
              newCalendarEventVersion,
              newCalendarEvent,
              visualParticipants,
              data.isRsvp,
              data.colorIsNop,
            );

            try {
              const updateUrl = addQueryToPath(
                `${calendarVersion.url}/calendar_event_versions/${newCalendarEventVersion.uid}`,
                {is_rsvp: data.isRsvp, send_notifications: data.shouldNotifyGuests},
              );
              const serverDataGroup = await putApiAsync(ApiCalendarEventVersionDataGroup, updateUrl, body);

              return updateDataAction(serverDataGroup);
            } catch (error) {
              // TODO: Handle save error by setting error status on calendarEventVersion
              if (!(error instanceof HttpError)) {
                reportToBugsnag(
                  new ExtendedError('CEV optimisticUpdateAsyncAction performer failure', error),
                );
              }
              throw error;
            }
          }),
      ),
    );
  };
}

function prepareSaveData(data: CalendarEventSaveData, state: AppState): CalendarEventSaveData {
  const {visualParticipants, recurringSaveType} = data;
  const newCalendarEvent = {
    ...data.newCalendarEvent,
    participants: visualParticipants.map((p) => buildParticipantFromVisualParticipant(p)),
  };

  switch (recurringSaveType) {
    case RecurringEventSaveTypesEnum.INSTANCE: {
      // If this is an existing recurring master event and the user has chosen to only edit this event in the series,
      // we have to build a new exception event to be created.
      if (isRecurringMasterEvent(newCalendarEvent)) {
        // newCalendarEvent and newCalendarEventVersion are mutated copies of the master.
        // Fetch the actual master event and event version to create the instance event.
        const masterCalendarEvent = getCalendarEvents(state)[data.newCalendarEvent.uid];
        const masterCalendarEventVersion = getCalendarEventVersions(state)[data.newCalendarEventVersion.uid];

        if (!masterCalendarEvent || !masterCalendarEventVersion) {
          throw new Error('Cannot create instance without the master calendarEvent and calendarEventVersion');
        }

        return prepareInstanceForMaster(
          masterCalendarEvent,
          masterCalendarEventVersion,
          data,
          getCompany(state),
        );
      }

      // If this event is already an exception in the series, we can just update it directly.
      if (isRecurringExceptionEvent(newCalendarEvent)) {
        return data;
      }

      throw new Error('Cannot apply a change to recurring instance event without master or exception.');
    }
    case RecurringEventSaveTypesEnum.ENTIRE_SERIES: {
      // If we're updating the entire series, we have to perform the update on the master event.
      if (isRecurringMasterEvent(newCalendarEvent)) {
        return data;
      }

      if (isRecurringExceptionEvent(newCalendarEvent)) {
        return prepareMasterForInstance(data, state);
      }

      throw new Error('Cannot apply a change to an entire series without a master or exception event.');
    }
    // case RecurringEventSaveTypesEnum.FUTURE_EVENTS: {
    //   // TODO: Handle this case by:
    //   // 1. Get the master event and update it's UNTIL recurrence value.
    //   // 2. Create a new recurring series.
    //   // 3. Delete the exception event if we're editing one.
    default:
      // If there are no special recurring cases to be handled, just return the event data.
      return data;
  }
}

function prepareInstanceForMaster(
  masterCalendarEvent: CalendarEventModel,
  masterCalendarEventVersion: CalendarEventVersionModel,
  data: CalendarEventSaveData,
  company: CompanyModel,
) {
  const {calendarVersion, newCalendarEvent, newCalendarEventVersion, instanceStartDate} = data;

  if (masterCalendarEvent.visualStatus !== CalendarEventVisualStatusesEnum.COMPLETE) {
    throw new Error('Cannot create an exception event from an incomplete master event.');
  }

  if (!instanceStartDate) {
    throw new Error('An instance start date is required to create an exception event.');
  }

  const [calendarEventToSave, calendarEventVersionToSave] = buildExceptionEventFromMaster(
    calendarVersion,
    masterCalendarEvent,
    masterCalendarEventVersion,
    newCalendarEvent,
    newCalendarEventVersion,
    instanceStartDate,
    company,
  );

  return {
    ...data,
    newCalendarEvent: calendarEventToSave,
    newCalendarEventVersion: calendarEventVersionToSave,
  };
}

function prepareMasterForInstance(data: CalendarEventSaveData, state: AppState) {
  const {calendarVersion, newCalendarEvent, newCalendarEventVersion} = data;

  if (!newCalendarEvent.recurringCalendarEventId) {
    throw new Error('Instance event required to prepare master event');
  }

  const [masterCalendarEvent, masterCalendarEventVersion] = retrieveMasterEventForId(
    getMasterCalendarEventIdToUidMap(state),
    getCalendarEvents(state),
    getCalendarEventVersions(state),
    newCalendarEvent.recurringCalendarEventId,
    calendarVersion.id,
  );

  const [calendarEventToSave, calendarEventVersionToSave] = applyInstanceChangesToMaster(
    masterCalendarEvent,
    masterCalendarEventVersion,
    newCalendarEvent,
    newCalendarEventVersion,
  );

  return {
    ...data,
    newCalendarEvent: calendarEventToSave,
    newCalendarEventVersion: calendarEventVersionToSave,
  };
}

export function deleteCalendarEventVersionAction(data: CalendarEventDeleteData): ThunkAction {
  return async (dispatch, getState) => {
    const {calendarEventData} = data;
    const {calendarVersion, calendarEventVersion, calendarEvent} = calendarEventData;

    const calenderEventVersionId = calendarEventVersion.uid;
    const state = getState();

    const optimisticUpdater = createObjectPropertyUpdater<DataState, 'calendarEventVersions'>(
      'calendarEventVersions',
      createResourceMapValueUpdater(
        calenderEventVersionId,
        createUpdater<CalendarEventVersionModel>((calendarEventVersionToRemove) => {
          const removedCalendarEventVersion = prepareDeletedCalendarEventVersion(
            dispatch,
            state,
            data,
            calendarEvent,
            calendarEventVersionToRemove,
            calendarVersion,
          );

          if (!removedCalendarEventVersion) {
            return calendarEventVersionToRemove;
          }

          return removedCalendarEventVersion;
        }),
      ),
    );

    await dispatch(
      optimisticUpdateAsyncAction(optimisticUpdater, async () =>
        findCalendarEventVersionLock(calenderEventVersionId).lockAsync(async () => {
          const {calendarEventVersions} = getOriginalData(state);

          const existingCalendarEventVersion = calendarEventVersions[calenderEventVersionId];

          if (!existingCalendarEventVersion) {
            throw new Error('A calendar event version with this ID could not be found.');
          }
          const removedCalendarEventVersion = prepareDeletedCalendarEventVersion(
            dispatch,
            state,
            data,
            calendarEvent,
            existingCalendarEventVersion,
            calendarVersion,
          );
          // If this an unsaved draft, just remove the version from our state.
          if (isUnsavedDraftCalendarEventVersion(calendarEventVersion)) {
            const action: CalendarEventVersionDeletedAction = {
              type: CalendarEventVersionActionsEnum.CALENDAR_EVENT_VERSION_DELETED,
              payload: calendarEventVersion.uid,
            };
            return action;
          }

          if (!removedCalendarEventVersion) {
            return undefined;
          }
          try {
            const resourceUrl = addQueryToPath(
              `${calendarVersion.url}/calendar_event_versions/${removedCalendarEventVersion.uid}`,
              {send_notifications: data.shouldNotifyGuests},
            );
            const serverDataGroup = await deleteApiAsync(ApiCalendarEventVersionDataGroup, resourceUrl);
            return updateDataAction(serverDataGroup);
          } catch (error) {
            if (!(error instanceof HttpError)) {
              reportToBugsnag(error);
            }
            throw error;
          }
        }),
      ),
    );
  };
}

function prepareDeletedCalendarEventVersion(
  dispatch: Dispatch,
  state: AppState,
  data: CalendarEventDeleteData,
  calendarEvent: CalendarEventModel,
  calendarEventVersion: CalendarEventVersionModel,
  calendarVersion: CalendarVersionModel,
) {
  const removedCalendarEventVersion: CalendarEventVersionModel = {
    ...calendarEventVersion,
    isRemoved: true,
  };

  switch (data.recurringSaveType) {
    case RecurringEventSaveTypesEnum.INSTANCE: {
      // If this is a master event and we're trying to remove an instance, we actually need to save a newly generated exception event with removed set to true.
      if (isRecurringMasterEvent(calendarEvent)) {
        saveRemovedInstanceEvent(dispatch, data, calendarVersion, calendarEvent, removedCalendarEventVersion);
        return null;
      }

      // If this is an exception event and we're removing a single instance, just return this exception to be deleted.
      if (calendarEvent.recurringCalendarEventId) {
        return removedCalendarEventVersion;
      }

      throw new Error('Cannot delete a recurring instance without an exception or master event.');
    }
    case RecurringEventSaveTypesEnum.ENTIRE_SERIES: {
      // If we already have the master event and we're deleting the entire series, just return the master event to be deletd.
      if (isRecurringMasterEvent(calendarEvent)) {
        return removedCalendarEventVersion;
      }

      // If this is an exception event and we're deleting the entire series, retrieve the master event return it to be deleted.
      if (calendarEvent.recurringCalendarEventId) {
        const [, masterCalendarEventVersion] = retrieveMasterEventForId(
          getMasterCalendarEventIdToUidMap(state),
          getCalendarEvents(state),
          getCalendarEventVersions(state),
          calendarEvent.recurringCalendarEventId,
          calendarVersion.id,
        );
        return {
          ...masterCalendarEventVersion,
          isRemoved: true,
        };
      }

      throw new Error('Cannot delete a recurring series without an exception or master event.');
    }
    default:
      // If we're not dealing with any special recurrence cases, proceed to delete the event.
      return removedCalendarEventVersion;
  }
}

function saveRemovedInstanceEvent(
  dispatch: Dispatch,
  data: CalendarEventDeleteData,
  calendarVersion: CalendarVersionModel,
  calendarEvent: CalendarEventModel,
  removedCalendarEventVersion: CalendarEventVersionModel,
) {
  if (!data.calendarEventData.instanceStartDate) {
    throw new Error('Cannot delete a recurring instance without an instance start date.');
  }

  if (!data.visualParticipants) {
    throw new Error('Cannot delete an recurring instance without visualParticipants.');
  }

  const newCalendarEventVersion = {
    ...removedCalendarEventVersion,
    ...buildCalendarEventVersionStartEndFromInstanceStart(
      removedCalendarEventVersion,
      data.calendarEventData.instanceStartDate,
    ),
  };

  dispatch(
    saveCalendarEventVersionAction({
      calendarVersion,
      newCalendarEventVersion,
      newCalendarEvent: calendarEvent,
      visualParticipants: data.visualParticipants,
      instanceStartDate: data.calendarEventData.instanceStartDate,
      recurringSaveType: RecurringEventSaveTypesEnum.INSTANCE,
      shouldNotifyGuests: data.shouldNotifyGuests,
    }),
  );
}

export function rescheduleCalendarEventVersionAction(
  eventParameters: CalendarEventGridParameters,
  startEndData: StartEndData,
  shouldNotifyGuests?: boolean,
): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();

    const getCalendarEventData = makeGetCalendarEventData(() => eventParameters);
    const calendarEventData = getCalendarEventData(state, {});
    if (!calendarEventData) {
      return;
    }

    const getVisualParticipants = makeGetVisualParticipants(
      getUserNamespace,
      () => calendarEventData.calendarEvent,
    );
    const visualParticipants = getVisualParticipants(state, {});

    // Apply the startEnd updates to the calendarEventVersion.
    const newCalendarEventVersion = {
      ...calendarEventData.calendarEventVersion,
      ...buildCalendarEventVersionStartEnd(startEndData),
    };

    const saveData = {
      newCalendarEventVersion,
      newCalendarEvent: calendarEventData.calendarEvent,
      calendarVersion: calendarEventData.calendarVersion,
      visualParticipants,
      instanceStartDate: calendarEventData.instanceStartDate,
      // TODO: Allow date modification to recurring series events
      recurringSaveType: isRecurringSaveTypeRequired(calendarEventData.calendarEvent)
        ? RecurringEventSaveTypesEnum.INSTANCE
        : undefined,
      shouldNotifyGuests,
    };

    trackSaveInterfaceEvent(saveData, getCalendars(state));

    dispatch(saveCalendarEventVersionAction(saveData));
  };
}

/*
 * Union type.
 */

export type CalendarEventVersionsAction = CalendarEventVersionDeletedAction;
